import { Link } from "@tanstack/react-router";
import { Header, Footer } from "@/components/layout";
import { PriceHistorySparkline } from "@/components/price-history-sparkline";
import { MonthlyPriceChart } from "@/components/monthly-price-chart";
import { CityTopDealsCard } from "@/components/city-top-deals-card";
import { TravelpayoutsWidget } from "@/components/travelpayouts-widget";
import { SearchClickStats } from "@/components/search-click-stats";
import { ArrowRight, Plane, Ruler, Clock } from "lucide-react";
import type { SeoPageData } from "@/lib/seo-page-loader";
import { isUnsupportedPair, TOP_DEPARTURE_CITIES, TOP_DESTINATIONS, THEMES, cityFromGen, cityToGen, countryGen, fixPolishGrammar } from "@/lib/seo-routes";
import { findAirport, countrySlug, airportsByCountry, allCountries } from "@/lib/airports";
import { distanceBetween, estimateFlightDuration } from "@/lib/airport-coords";
import { CURATED_CITY_CODES } from "@/lib/curated-city-guides";

interface Props {
  type: "city" | "route" | "country" | "theme";
  from?: string;
  to?: string;
  fromName?: string;
  toName?: string;
  searchHref: string;
  page: SeoPageData | null;
  fallbackH1: string;
  /** Domyślna data wylotu (ISO) — dopina do głównego SearchForm. */
  dd?: string;
  /** Domyślna data powrotu (ISO) — dopina do głównego SearchForm. */
  rd?: string;
  /** Domyślny kierunek (IATA) — dopina do głównego SearchForm na stronach miast. */
  defaultTo?: string;
  /** Liczba dorosłych pasażerów — dopina do głównego SearchForm. */
  adults?: number;
  /** Nazwa kraju (dla landingów typu „country") — używana do linków wewnętrznych. */
  countryName?: string;
  /** Slug tematu (dla landingów typu „theme") — żeby pokazać inne tematy. */
  themeSlug?: string;
}

export function SeoPageView({ type, from, to, fromName, toName, searchHref, page, fallbackH1, dd, rd, defaultTo, adults, countryName, themeSlug }: Props) {
  // Breadcrumbs jako struktura — z linkiem do /loty na każdym typie landingu.
  // Dla "route" dodatkowo link do strony miasta wylotu.
  const crumbTail =
    type === "route" ? `${fromName ?? from} → ${toName ?? to}` :
    type === "city"  ? fixPolishGrammar(`Loty z ${cityFromGen(fromName ?? "")}`) :
    type === "country" && countryName ? `Loty do ${countryGen(countryName)}` :
    fallbackH1;

  // Używamy krótkiego fallbackH1 zamiast page.h1 z AI — krótszy nagłówek
  // jest czytelniejszy i lepiej wygląda na mobile. Pełna fraza SEO i tak
  // jest w <title>, meta description oraz w intro/sekcjach poniżej.
  // fixPolishGrammar koryguje też „z Szczecina" → „ze Szczecina".
  const h1 = fixPolishGrammar(fallbackH1);

  return (
    <div className="min-h-screen">
      <Header />
      <article className="container mx-auto max-w-4xl px-4 py-5 md:py-10">
        <nav className="mb-3 text-sm text-muted-foreground md:mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground">Tanie Latanie</Link>
          {" / "}
          <Link to="/loty" className="hover:text-foreground">Loty</Link>
          {" / "}
          {type === "route" && from && (
            <>
              <Link to="/loty/$city" params={{ city: from.toLowerCase() }} className="hover:text-foreground">
                Loty z {cityFromGen(fromName ?? "")}
              </Link>
              {" / "}
            </>
          )}
          <span>{crumbTail}</span>
        </nav>

        <header className="mb-6 md:mb-8">
          {/* Mobilnie: H1 mniejszy + krótszy odstęp do formularza, żeby
              cały SearchForm mieścił się nad foldem na ~390x844 (iPhone). */}
          <h1 className="font-display text-2xl font-bold leading-tight sm:text-3xl md:text-5xl">{h1}</h1>
          {from && (
            <div className="mt-3 md:mt-6">
              <TravelpayoutsWidget
                origin={from}
                destination={type === "route" ? to : type === "country" ? defaultTo : undefined}
              />
              <SearchClickStats />
            </div>
          )}
        </header>

        {/* Karty unikalne per trasa — renderujemy ZAWSZE (nawet zanim AI
            wygeneruje landing). Dzięki temu strona ma własną treść:
            dystans, czas lotu, miesięczne ceny, top linie. To eliminuje
            "thin content" i poprawia indexability w Google. */}
        {type === "route" && from && to && (
          <>
            <RouteDistanceCard from={from} to={to} fromName={fromName} toName={toName} />
            <PriceHistorySparkline from={from} to={to} className="mt-6" />
            <MonthlyPriceChart from={from} to={to} fromName={fromName} toName={toName} />
          </>
        )}
        {type === "city" && from && (
          <CityTopDealsCard from={from} fromName={fromName} limit={20} />
        )}

        {page ? (
          <>
            <div className="prose prose-invert max-w-none mt-8">
              <p className="text-lg leading-relaxed text-foreground/90 whitespace-pre-line">
                {fixPolishGrammar(page.intro)}
              </p>
            </div>

            <Section title={page.section_titles?.description ?? "Opis"} body={fixPolishGrammar(page.sections.description)} />
            <Section title={page.section_titles?.best_time ?? "Najlepszy czas na lot"} body={fixPolishGrammar(page.sections.best_time)} />
            <Section title="Jak sprawdzić cenę biletu?" body="Wybierz daty i liczbę pasażerów w wyszukiwarce Aviasales. Ceny zapisane wcześniej mogą różnić się od aktualnej oferty. Zakres bagażu, warunki taryfy i cenę końcową potwierdź u sprzedawcy przed płatnością." />
            <Section title={page.section_titles?.tips ?? "Praktyczne porady"} body={fixPolishGrammar(page.sections.tips)} />
            {page.editorial_sections?.map((section) => <Section key={section.title} title={section.title} body={fixPolishGrammar(section.body)} />)}
            {page.sources && page.sources.length > 0 && (
              <section className="mt-8">
                <h2 className="mb-3 font-display text-xl font-bold">Źródła i aktualne informacje</h2>
                <ul className="space-y-2 text-sm">
                  {page.sources.map((source) => <li key={source.url}><a className="text-primary underline" href={source.url} target="_blank" rel="noopener noreferrer">{source.label}</a></li>)}
                </ul>
                <p className="mt-3 text-sm text-muted-foreground">Rozkłady, przystanki i warunki zwiedzania mogą się zmieniać. Sprawdź informacje u operatora przed podróżą.</p>
              </section>
            )}

            <section className="mt-10">
              <h2 className="mb-4 font-display text-2xl font-bold">Najczęściej zadawane pytania</h2>
              <div className="space-y-3">
                {page.faq.map((f, i) => (
                  <details key={i} className="glass rounded-lg p-4">
                    <summary className="cursor-pointer font-semibold">{fixPolishGrammar(f.q)}</summary>
                    <p className="mt-2 text-muted-foreground">{fixPolishGrammar(f.a)}</p>
                  </details>
                ))}
              </div>
            </section>

            <div className="mt-10">
              <Link
                to={searchHref}
                className="inline-flex rounded-xl bg-gradient-sunset px-6 py-3 font-semibold text-primary-foreground shadow-glow"
              >
                Sprawdź ceny biletów
              </Link>
            </div>

            <RelatedRoutes type={type} from={from} to={to} fromName={fromName} toName={toName} countryName={countryName} themeSlug={themeSlug} />
          </>
        ) : (
          <>
            <p className="py-8 text-muted-foreground">
              Dostępność połączeń zależy od daty, liczby pasażerów i wybranej taryfy. Ceny zapisane wcześniej mogą się zmienić. Przed rezerwacją{" "}
              <Link to={searchHref} className="text-primary hover:underline">sprawdź aktualne ceny biletów</Link>.
            </p>
            <RelatedRoutes type={type} from={from} to={to} fromName={fromName} toName={toName} countryName={countryName} themeSlug={themeSlug} />
          </>
        )}
      </article>
      <Footer />
    </div>
  );
}

function Section({ title, body }: { title: string; body: string }) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 font-display text-2xl font-bold">{title}</h2>
      <p className="whitespace-pre-line text-foreground/85">{body}</p>
    </section>
  );
}

function RouteDistanceCard({
  from, to, fromName, toName,
}: { from: string; to: string; fromName?: string; toName?: string }) {
  const km = distanceBetween(from, to);
  if (!km) return null;
  const dur = estimateFlightDuration(km);
  const af = findAirport(from.toUpperCase());
  const at = findAirport(to.toUpperCase());
  const speed = 820; // km/h orientacyjna prędkość rejsowa
  return (
    <section className="mt-8 rounded-xl border border-primary/30 bg-card/50 p-5">
      <div className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
        <Plane className="h-4 w-4" /> Trasa i odległość
      </div>

      <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-background/40 p-4">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">Wylot</div>
          <div className="truncate font-display text-base font-semibold">
            {fromName ?? af?.city} <span className="text-muted-foreground">({from.toUpperCase()})</span>
          </div>
          {af?.name && !af.isMetro && <div className="truncate text-xs text-muted-foreground">im. {af.name}</div>}
        </div>
        <ArrowRight className="h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 text-right">
          <div className="text-xs text-muted-foreground">Przylot</div>
          <div className="truncate font-display text-base font-semibold">
            {toName ?? at?.city} <span className="text-muted-foreground">({to.toUpperCase()})</span>
          </div>
          {at?.name && !at.isMetro && <div className="truncate text-xs text-muted-foreground">im. {at.name}</div>}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg bg-background/40 p-4">
          <div className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Ruler className="h-3 w-3 text-primary" /> Odległość
          </div>
          <div className="font-display text-lg font-bold">{km.toLocaleString("pl-PL")} km</div>
          <div className="text-xs text-muted-foreground">w linii prostej (po wielkim kole)</div>
        </div>
        <div className="rounded-lg bg-background/40 p-4">
          <div className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3 text-primary" /> Czas lotu
          </div>
          <div className="font-display text-lg font-bold">{dur.label}</div>
          <div className="text-xs text-muted-foreground">lot bezpośredni, orientacyjnie</div>
        </div>
        <div className="rounded-lg bg-background/40 p-4">
          <div className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Plane className="h-3 w-3 text-primary" /> Prędkość rejsowa
          </div>
          <div className="font-display text-lg font-bold">~{speed} km/h</div>
          <div className="text-xs text-muted-foreground">typowy samolot pasażerski</div>
        </div>
      </div>

      <p className="mt-4 text-sm text-muted-foreground">
        Szacunek {dur.label} wynika z odległości w linii prostej; nie potwierdza dostępności lotu bezpośredniego.
        Rzeczywisty czas podróży i przesiadki sprawdź w wybranym połączeniu.
      </p>
    </section>
  );
}

function RelatedRoutes({
  type, from, to, fromName, toName, countryName, themeSlug,
}: { type: Props["type"]; from?: string; to?: string; fromName?: string; toName?: string; countryName?: string; themeSlug?: string }) {
  if (type === "route" && from && to) {
    const sameFrom: { label: string; href: string }[] = [];
    for (const d of TOP_DESTINATIONS) {
      if (d === to || d === from || isUnsupportedPair(from, d)) continue;
      const a = findAirport(d);
      if (!a) continue;
      sameFrom.push({
        label: `${fromName} → ${a.city}`,
        href: `/loty/${from.toLowerCase()}/${d.toLowerCase()}`,
      });
      if (sameFrom.length >= 8) break;
    }
    const sameTo: { label: string; href: string }[] = [];
    for (const c of TOP_DEPARTURE_CITIES) {
      if (c === from || isUnsupportedPair(c, to)) continue;
      const a = findAirport(c);
      if (!a) continue;
      sameTo.push({
        label: `${a.city} → ${toName}`,
        href: `/loty/${c.toLowerCase()}/${to.toLowerCase()}`,
      });
      if (sameTo.length >= 6) break;
    }
    const destAirport = findAirport(to);
    const themeItems = THEMES.slice(0, 6).map((t) => ({
      label: t.label,
      href: `/loty/temat/${t.slug}`,
    }));
    const countryItems: { label: string; href: string }[] = [];
    if (destAirport) {
      countryItems.push({
        label: `Wszystkie loty do ${countryGen(destAirport.country)}`,
        href: `/loty/kraj/${countrySlug(destAirport.country)}`,
      });
    }
    return (
      <>
        <RelatedBlock title={fixPolishGrammar(`Inne loty z ${cityFromGen(fromName ?? "")}`)} items={sameFrom} />
        <RelatedBlock title={fixPolishGrammar(`Loty do ${cityToGen(toName ?? "")} z innych miast`)} items={sameTo} />
        {countryItems.length > 0 && <RelatedBlock title="Cały kraj docelowy" items={countryItems} />}
        <RelatedBlock title="Inspiracje tematyczne" items={themeItems} />
      </>
    );
  }

  if (type === "city" && from) {
    const items: { label: string; href: string }[] = [];
    for (const d of TOP_DESTINATIONS) {
      if (d === from || isUnsupportedPair(from, d)) continue;
      const a = findAirport(d);
      if (!a) continue;
      items.push({
        label: `${fromName} → ${a.city}`,
        href: `/loty/${from.toLowerCase()}/${d.toLowerCase()}`,
      });
      if (items.length >= 12) break;
    }
    const guides = CURATED_CITY_CODES.filter((code) => code !== from).map((code) => {
      const airport = findAirport(code);
      return { label: airport ? `${airport.city} — poradnik lotniskowy` : code, href: `/loty/${code.toLowerCase()}` };
    });
    return <><RelatedBlock title={fixPolishGrammar(`Popularne kierunki z ${cityFromGen(fromName ?? "")}`)} items={items} /><RelatedBlock title="Poradniki innych miast" items={guides} /></>;
  }

  // === COUNTRY landing: lotniska w kraju + departures TO kraj + inne kraje + tematy
  if (type === "country" && countryName) {
    const dests = airportsByCountry(countryName).slice(0, 10);
    const polishHub = from ?? "WAW";
    const airportItems = dests.filter((a) => !isUnsupportedPair(polishHub, a.code)).map((a) => ({
      label: `${polishHub} → ${a.city} (${a.code})`,
      href: `/loty/${polishHub.toLowerCase()}/${a.code.toLowerCase()}`,
    }));
    const firstDest = dests[0]?.code.toLowerCase();
    const departureItems: { label: string; href: string }[] = firstDest
      ? TOP_DEPARTURE_CITIES.filter((c) => !isUnsupportedPair(c, firstDest)).slice(0, 8).map((c) => {
          const a = findAirport(c);
          return {
            label: fixPolishGrammar(`${prepFromOrZ(a?.city ?? c)} ${cityFromGen(a?.city ?? c)} do ${countryGen(countryName)}`),
            href: `/loty/${c.toLowerCase()}/${firstDest}`,
          };
        })
      : [];
    const otherCountries = allCountries()
      .filter((c) => c !== countryName)
      .slice(0, 8)
      .map((c) => ({
        label: `Loty do ${countryGen(c)}`,
        href: `/loty/kraj/${countrySlug(c)}`,
      }));
    const themeItems = THEMES.slice(0, 6).map((t) => ({
      label: t.label,
      href: `/loty/temat/${t.slug}`,
    }));
    return (
      <>
        <RelatedBlock title={`Lotniska w ${countryGen(countryName)}`} items={airportItems} />
        <RelatedBlock title={`Loty do ${countryGen(countryName)} z innych miast`} items={departureItems} />
        <RelatedBlock title="Inne kraje do odkrycia" items={otherCountries} />
        <RelatedBlock title="Inspiracje tematyczne" items={themeItems} />
      </>
    );
  }

  // === THEME landing: popularne kierunki + miasta wylotu + inne tematy
  if (type === "theme") {
    const polishHub = from ?? "WAW";
    const dests: { label: string; href: string }[] = [];
    for (const d of TOP_DESTINATIONS) {
      if (d === polishHub || isUnsupportedPair(polishHub, d)) continue;
      const a = findAirport(d);
      if (!a) continue;
      dests.push({
        label: `${a.city} (${a.code})`,
        href: `/loty/${polishHub.toLowerCase()}/${d.toLowerCase()}`,
      });
      if (dests.length >= 12) break;
    }
    const cityItems = TOP_DEPARTURE_CITIES.slice(0, 10).map((c) => {
      const a = findAirport(c);
      return {
        label: fixPolishGrammar(`Loty z ${cityFromGen(a?.city ?? c)}`),
        href: `/loty/${c.toLowerCase()}`,
      };
    });
    const otherThemes = THEMES
      .filter((t) => t.slug !== themeSlug)
      .slice(0, 8)
      .map((t) => ({ label: t.label, href: `/loty/temat/${t.slug}` }));
    return (
      <>
        <RelatedBlock title="Popularne kierunki na ten typ wyjazdu" items={dests} />
        <RelatedBlock title="Wyleć z innego miasta" items={cityItems} />
        <RelatedBlock title="Inne inspiracje" items={otherThemes} />
      </>
    );
  }

  const items: { label: string; href: string }[] = [];
  for (const c of TOP_DEPARTURE_CITIES) {
    const a = findAirport(c);
    if (!a) continue;
    items.push({ label: fixPolishGrammar(`Loty z ${cityFromGen(a.city)}`), href: `/loty/${c.toLowerCase()}` });
  }
  return <RelatedBlock title="Wybierz miasto wylotu" items={items.slice(0, 12)} />;
}

// Helper: „z" vs „ze" przed deklinacją miasta (np. „ze Szczecina").
function prepFromOrZ(city: string): "z" | "ze" {
  const declined = cityFromGen(city);
  return /^(?:[SsZzŚśŻżŹź][a-ząćęłńóśźż]|Wsz|wsz)/.test(declined) &&
    !/^(?:[SsZzŚśŻżŹź][aąeęioóuy])/.test(declined) ? "ze" : "z";
}

function RelatedBlock({ title, items }: { title: string; items: { label: string; href: string }[] }) {
  if (items.length === 0) return null;
  return (
    <section className="mt-10">
      <h2 className="mb-4 font-display text-xl font-bold">{title}</h2>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((it) => (
          <a
            key={it.href}
            href={it.href}
            className="inline-flex items-center justify-between rounded-lg border border-border/50 bg-card/40 px-3 py-2 text-sm transition hover:border-primary/40 hover:bg-card/70"
          >
            <span>{it.label}</span>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
          </a>
        ))}
      </div>
    </section>
  );
}
